import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Clock, UserCheck, ShieldCheck, FileText, PhoneCall, BadgeCheck, Share2, LayoutDashboard,
} from "lucide-react";
import { LOGO_URL, shareFormWhatsApp } from "@/lib/brand";
import { api } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/4609052/pexels-photo-4609052.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const TRUST = [
  { icon: Clock, title: "Rappel sous 48h", text: "Une conseillère dédiée vous recontacte rapidement." },
  { icon: UserCheck, title: "Accompagnement humain", text: "Un interlocuteur unique, présent partout en France." },
  { icon: ShieldCheck, title: "Simple & sécurisé", text: "Vos données sont transmises en toute confidentialité." },
];

const STEPS = [
  { icon: FileText, title: "Remplissez votre fiche", text: "2 minutes, depuis votre mobile, en quelques clics." },
  { icon: PhoneCall, title: "Soyez rappelé(e)", text: "Notre conseillère étudie votre dossier et vous appelle." },
  { icon: BadgeCheck, title: "Réalisez des économies", text: "Nous négocions les meilleures offres du marché et vous accompagnons pour réduire durablement vos factures." },
];

export default function Home() {
  const navigate = useNavigate();
  const ref = new URLSearchParams(window.location.search).get("ref");
  const [refName, setRefName] = useState(null);

  useEffect(() => {
    if (ref) api.get(`/ref/${ref}`).then((r) => { if (r.data?.name) setRefName(r.data.name); }).catch(() => {});
  }, [ref]);

  const goToForm = () =>
    navigate(ref ? `/formulaire?ref=${encodeURIComponent(ref)}` : "/formulaire", { state: { fromLanding: true } });

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" data-testid="home-logo-link">
            <img src={LOGO_URL} alt="Place des Énergies" className="h-14 w-auto sm:h-16" />
          </Link>
          <Link
            to="/admin/login"
            data-testid="advisor-space-link"
            className="flex h-10 items-center gap-2 rounded-full border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:border-primary hover:text-primary"
          >
            <LayoutDashboard className="h-4 w-4" />
            <span className="hidden sm:inline">Espace conseillère</span>
            <span className="sm:hidden">Conseillère</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="pde-grid-bg">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-12 sm:px-8 lg:grid-cols-2 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {refName && (
              <p className="mb-4" data-testid="ref-invite-banner">
                <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                  <UserCheck className="h-3.5 w-3.5" /> Vous êtes invité(e) par {refName}
                </span>
              </p>
            )}
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              <BadgeCheck className="h-3.5 w-3.5" /> Courtier en énergie depuis 2013
            </span>
            <h1 className="mt-5 font-heading text-4xl font-bold leading-tight text-foreground sm:text-5xl lg:text-6xl">
              Payez votre énergie<br />au juste prix.
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground sm:text-lg">
              Remplissez votre fiche en 2 minutes. Notre conseillère vous rappelle sous 48h pour
              comparer les offres et optimiser votre contrat d'électricité et de gaz.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                data-testid="cta-fill-form"
                onClick={goToForm}
                className="group flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary/90 active:scale-[0.99]"
              >
                Remplir ma fiche de contact
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                type="button"
                data-testid="cta-whatsapp-share"
                onClick={shareFormWhatsApp}
                className="flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
              >
                <Share2 className="h-4 w-4 text-whatsapp-dark" />
                Partager via WhatsApp
              </button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative"
          >
            <div className="overflow-hidden rounded-3xl border border-slate-200 shadow-xl">
              <img src={HERO_IMG} alt="Famille à la maison" className="h-72 w-full object-cover sm:h-96 lg:h-[460px]" />
            </div>
            <div className="absolute -bottom-5 left-5 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white p-3.5 shadow-lg sm:left-8">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-bold text-foreground">Rappel sous 48h</p>
                <p className="text-xs text-muted-foreground">par une conseillère dédiée</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust */}
      <section className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
        <div className="grid gap-5 sm:grid-cols-3">
          {TRUST.map((t) => (
            <div key={t.title} className="rounded-2xl border border-slate-200 bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <t.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-foreground">{t.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
          <h2 className="text-center font-heading text-3xl font-bold text-foreground sm:text-4xl">
            Comment ça marche ?
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-center text-base text-muted-foreground">
            Trois étapes simples pour reprendre le contrôle de votre budget énergie.
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <div key={s.title} className="relative rounded-2xl border border-slate-200 bg-white p-7">
                <span className="absolute -top-4 left-7 flex h-9 w-9 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">
                  {i + 1}
                </span>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <s.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-heading text-lg font-bold text-foreground">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <button
              type="button"
              data-testid="cta-fill-form-bottom"
              onClick={() => navigate("/formulaire")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary/90 active:scale-[0.99]"
            >
              Remplir ma fiche de contact
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row sm:px-8">
          <img src={LOGO_URL} alt="Place des Énergies" className="h-12 w-auto" />
          <div className="flex items-center gap-5 text-sm">
            <a href="https://placedesenergies.com" target="_blank" rel="noreferrer" className="font-medium text-slate-600 hover:text-primary">
              placedesenergies.com
            </a>
            <Link to="/admin/login" data-testid="footer-advisor-link" className="font-medium text-slate-600 hover:text-primary">
              Espace conseillère
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
